import { CoreError, err, ok, type Result } from './result';
import type { Money } from './money';

/** Opaque identifier of a group member (account or placeholder). */
export type MemberId = string;

/** An amount in minor units attributed to one member (a share, or what a payer paid). */
export interface Allocation {
  readonly memberId: MemberId;
  /** Safe integer, minor units of the expense currency. */
  readonly amount: number;
}

export type SplitMode = 'equal' | 'exact' | 'percent' | 'shares';

/** Basis points: 1 % = 100 bp, 100 % = 10 000 bp. Lets the UI accept "33.33 %" without floats. */
export type BasisPoints = number;

export const FULL_PERCENT_BP = 10_000;

export interface PercentEntry {
  readonly memberId: MemberId;
  /** Non-negative integer basis points; all entries must sum to 10 000. */
  readonly basisPoints: BasisPoints;
}

export interface SharesEntry {
  readonly memberId: MemberId;
  /**
   * Non-negative integer weight. Fractional shares (0.5) must be scaled to integers by the
   * caller (e.g. 1 and 0.5 → 2 and 1); the result is identical.
   */
  readonly shares: number;
}

/** How an expense's total is divided among its participants. */
export type SplitSpec =
  | { readonly mode: 'equal'; readonly members: readonly MemberId[] }
  | { readonly mode: 'exact'; readonly amounts: readonly Allocation[] }
  | { readonly mode: 'percent'; readonly percents: readonly PercentEntry[] }
  | { readonly mode: 'shares'; readonly shares: readonly SharesEntry[] };

export type SplitError =
  | { readonly code: 'NO_PARTICIPANTS' }
  | { readonly code: 'DUPLICATE_MEMBER'; readonly memberId: MemberId }
  | { readonly code: 'INVALID_TOTAL'; readonly total: number }
  | { readonly code: 'INVALID_VALUE'; readonly memberId: MemberId; readonly value: number }
  | { readonly code: 'EXACT_SUM_MISMATCH'; readonly expected: number; readonly actual: number }
  | { readonly code: 'PERCENT_SUM_MISMATCH'; readonly expected: number; readonly actual: number }
  | { readonly code: 'ZERO_TOTAL_SHARES' }
  | { readonly code: 'NO_PAYERS' }
  | { readonly code: 'PAYERS_SUM_MISMATCH'; readonly expected: number; readonly actual: number };

export type SplitErrorCode = SplitError['code'];

/** Deterministic member-id ordering (UTF-16 code unit order, locale independent). */
export function compareMemberIds(a: MemberId, b: MemberId): number {
  return a < b ? -1 : a > b ? 1 : 0;
}

export interface Weight {
  readonly memberId: MemberId;
  /** Non-negative safe integer. */
  readonly weight: number;
}

/**
 * Splits `total` (minor units, any sign) proportionally to integer `weights` using the
 * largest remainder method:
 *
 * 1. each member gets `floor(|total| * w / W)`;
 * 2. the leftover units (always fewer than the number of members with weight > 0) are
 *    handed out one by one in order of largest fractional remainder, ties broken by
 *    ascending member id (`compareMemberIds`).
 *
 * The result always sums exactly to `total`, preserves input order, and is fully
 * deterministic. Negative totals are allocated as `-(allocation of |total|)`.
 * Uses BigInt internally so `total * weight` cannot overflow.
 *
 * Throws `CoreError` on invalid input (programmer error); `computeShares` validates first.
 */
export function allocateProportionally(total: number, weights: readonly Weight[]): Allocation[] {
  if (!Number.isSafeInteger(total)) throw new CoreError('INVALID_TOTAL', `Invalid total ${total}`);
  let weightSum = 0n;
  for (const w of weights) {
    if (!Number.isSafeInteger(w.weight) || w.weight < 0) {
      throw new CoreError('INVALID_WEIGHT', `Invalid weight ${w.weight} for ${w.memberId}`);
    }
    weightSum += BigInt(w.weight);
  }
  if (weightSum === 0n) {
    if (total === 0) return weights.map((w) => ({ memberId: w.memberId, amount: 0 }));
    throw new CoreError('ZERO_TOTAL_WEIGHT', 'Cannot allocate a non-zero total over zero weight');
  }

  const sign = total < 0 ? -1n : 1n;
  const abs = BigInt(Math.abs(total));
  const rows = weights.map((w, index) => {
    const product = abs * BigInt(w.weight);
    return {
      index,
      memberId: w.memberId,
      base: product / weightSum,
      remainder: product % weightSum,
    };
  });
  let leftover = abs - rows.reduce((acc, r) => acc + r.base, 0n);

  const order = [...rows].sort((a, b) => {
    if (a.remainder !== b.remainder) return a.remainder > b.remainder ? -1 : 1;
    return compareMemberIds(a.memberId, b.memberId) || a.index - b.index;
  });
  const extra = new Array<bigint>(rows.length).fill(0n);
  for (const row of order) {
    if (leftover === 0n) break;
    extra[row.index] = 1n;
    leftover -= 1n;
  }

  return rows.map((r) => {
    const amount = Number(sign * (r.base + (extra[r.index] ?? 0n)));
    return { memberId: r.memberId, amount: amount === 0 ? 0 : amount };
  });
}

function findDuplicate(ids: readonly MemberId[]): MemberId | undefined {
  const seen = new Set<MemberId>();
  for (const id of ids) {
    if (seen.has(id)) return id;
    seen.add(id);
  }
  return undefined;
}

function sumAmounts(items: readonly Allocation[]): number {
  return items.reduce((acc, a) => acc + a.amount, 0);
}

/**
 * Computes each participant's share of `total` for the given split.
 * Shares are returned in the spec's input order and always sum exactly to `total.amount`.
 */
export function computeShares(total: Money, spec: SplitSpec): Result<Allocation[], SplitError> {
  if (!Number.isSafeInteger(total.amount)) {
    return err({ code: 'INVALID_TOTAL', total: total.amount });
  }
  const t = total.amount;

  switch (spec.mode) {
    case 'equal': {
      if (spec.members.length === 0) return err({ code: 'NO_PARTICIPANTS' });
      const dup = findDuplicate(spec.members);
      if (dup !== undefined) return err({ code: 'DUPLICATE_MEMBER', memberId: dup });
      return ok(
        allocateProportionally(
          t,
          spec.members.map((memberId) => ({ memberId, weight: 1 })),
        ),
      );
    }

    case 'exact': {
      if (spec.amounts.length === 0) return err({ code: 'NO_PARTICIPANTS' });
      const dup = findDuplicate(spec.amounts.map((a) => a.memberId));
      if (dup !== undefined) return err({ code: 'DUPLICATE_MEMBER', memberId: dup });
      for (const a of spec.amounts) {
        const wrongSign = t >= 0 ? a.amount < 0 : a.amount > 0;
        if (!Number.isSafeInteger(a.amount) || wrongSign) {
          return err({ code: 'INVALID_VALUE', memberId: a.memberId, value: a.amount });
        }
      }
      const actual = sumAmounts(spec.amounts);
      if (actual !== t) return err({ code: 'EXACT_SUM_MISMATCH', expected: t, actual });
      return ok(
        spec.amounts.map((a) => ({ memberId: a.memberId, amount: a.amount === 0 ? 0 : a.amount })),
      );
    }

    case 'percent': {
      if (spec.percents.length === 0) return err({ code: 'NO_PARTICIPANTS' });
      const dup = findDuplicate(spec.percents.map((p) => p.memberId));
      if (dup !== undefined) return err({ code: 'DUPLICATE_MEMBER', memberId: dup });
      for (const p of spec.percents) {
        if (
          !Number.isSafeInteger(p.basisPoints) ||
          p.basisPoints < 0 ||
          p.basisPoints > FULL_PERCENT_BP
        ) {
          return err({ code: 'INVALID_VALUE', memberId: p.memberId, value: p.basisPoints });
        }
      }
      const actual = spec.percents.reduce((acc, p) => acc + p.basisPoints, 0);
      if (actual !== FULL_PERCENT_BP) {
        return err({ code: 'PERCENT_SUM_MISMATCH', expected: FULL_PERCENT_BP, actual });
      }
      return ok(
        allocateProportionally(
          t,
          spec.percents.map((p) => ({ memberId: p.memberId, weight: p.basisPoints })),
        ),
      );
    }

    case 'shares': {
      if (spec.shares.length === 0) return err({ code: 'NO_PARTICIPANTS' });
      const dup = findDuplicate(spec.shares.map((s) => s.memberId));
      if (dup !== undefined) return err({ code: 'DUPLICATE_MEMBER', memberId: dup });
      for (const s of spec.shares) {
        if (!Number.isSafeInteger(s.shares) || s.shares < 0) {
          return err({ code: 'INVALID_VALUE', memberId: s.memberId, value: s.shares });
        }
      }
      if (spec.shares.every((s) => s.shares === 0)) return err({ code: 'ZERO_TOTAL_SHARES' });
      return ok(
        allocateProportionally(
          t,
          spec.shares.map((s) => ({ memberId: s.memberId, weight: s.shares })),
        ),
      );
    }
  }
}

/** Members who take part in a split (including zero-weight entries). */
export function splitMembers(spec: SplitSpec): MemberId[] {
  switch (spec.mode) {
    case 'equal':
      return [...spec.members];
    case 'exact':
      return spec.amounts.map((a) => a.memberId);
    case 'percent':
      return spec.percents.map((p) => p.memberId);
    case 'shares':
      return spec.shares.map((s) => s.memberId);
  }
}

/**
 * Validates the payers of an expense: at least one, no duplicates, every amount a safe
 * integer with the same sign as the total, and the amounts summing exactly to the total.
 */
export function validatePayers(
  total: Money,
  payers: readonly Allocation[],
): Result<Allocation[], SplitError> {
  if (!Number.isSafeInteger(total.amount))
    return err({ code: 'INVALID_TOTAL', total: total.amount });
  if (payers.length === 0) return err({ code: 'NO_PAYERS' });
  const dup = findDuplicate(payers.map((p) => p.memberId));
  if (dup !== undefined) return err({ code: 'DUPLICATE_MEMBER', memberId: dup });
  for (const p of payers) {
    const wrongSign = total.amount >= 0 ? p.amount < 0 : p.amount > 0;
    if (!Number.isSafeInteger(p.amount) || wrongSign) {
      return err({ code: 'INVALID_VALUE', memberId: p.memberId, value: p.amount });
    }
  }
  const actual = sumAmounts(payers);
  if (actual !== total.amount) {
    return err({ code: 'PAYERS_SUM_MISMATCH', expected: total.amount, actual });
  }
  return ok(payers.map((p) => ({ memberId: p.memberId, amount: p.amount === 0 ? 0 : p.amount })));
}

/** Convenience: several people paid the total in equal parts (largest remainder rounding). */
export function payersEqually(total: Money, members: readonly MemberId[]): Allocation[] {
  return allocateProportionally(
    total.amount,
    members.map((memberId) => ({ memberId, weight: 1 })),
  );
}
